import { AsyncActionButton, BackButton } from '@applyonline/shared'
import { useState, type JSX, type KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'

type IncomeMethod = 'connect' | 'upload'

interface Props {
  onConnectBank: () => void
  onUploadDocuments: () => void
  isSubmittingUpload: boolean
}

/**
 * Page 1 of the simplified income-verification flow: the applicant selects between
 * connecting their bank (recommended) and uploading supporting documents, then a single
 * "Continue verification" action proceeds with the chosen method. The upload option is
 * only offered when the merchant allows skipping Flinks.
 */
const ChooseIncomeMethodPage = ({
  onConnectBank,
  onUploadDocuments,
  isSubmittingUpload,
}: Props): JSX.Element => {
  const { t } = useTranslation()
  const [selectedMethod, setSelectedMethod] = useState<IncomeMethod>('connect')

  const connectBenefits = [
    t('incomeMethod.connectBenefit1'),
    t('incomeMethod.connectBenefit2'),
    t('incomeMethod.connectBenefit3'),
    t('incomeMethod.connectBenefit4'),
  ]
  const uploadBenefits = [
    t('incomeMethod.uploadBenefit1'),
    t('incomeMethod.uploadBenefit2'),
    t('incomeMethod.uploadBenefit3'),
    t('incomeMethod.uploadBenefit4'),
  ]

  const selectMethod = (method: IncomeMethod) => () => setSelectedMethod(method)
  const selectOnKey = (method: IncomeMethod) => (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      setSelectedMethod(method)
    }
  }

  const handleContinue = () => {
    if (selectedMethod === 'upload') onUploadDocuments()
    else onConnectBank()
  }

  return (
    <>
      <h3 style={{ width: '100%', textAlign: 'center' }}>{t('incomeMethod.title')}</h3>
      <p className="paragraph" style={{ width: '100%', textAlign: 'center' }}>
        {t('incomeMethod.subtitle')}
      </p>

      <div className="income-method-group">
        <div
          role="radio"
          tabIndex={0}
          aria-checked={selectedMethod === 'connect'}
          className={`income-method-card ${selectedMethod === 'connect' ? 'income-method-card--selected' : ''}`}
          onClick={selectMethod('connect')}
          onKeyDown={selectOnKey('connect')}
        >
          <div className="income-method-card__header">
            <div className="income-method-card__heading">
              <i className="fa-solid fa-building-columns" />
              <h5>{t('incomeMethod.connectTitle')}</h5>
            </div>
            <span className="income-method-card__badge">{t('incomeMethod.recommended')}</span>
          </div>
          <p style={{paddingBottom:"1.5rem"}}>{t('incomeMethod.connectDescription')}</p>
          <ul className="income-method-card__benefits">
            {connectBenefits.map((benefit) => (
              <li key={benefit}>
                <i className="fa-regular fa-check" />
                {benefit}
              </li>
            ))}
          </ul>
        </div>
          <div
            role="radio"
            tabIndex={0}
            aria-checked={selectedMethod === 'upload'}
            className={`income-method-card ${selectedMethod === 'upload' ? 'income-method-card--selected' : ''}`}
            onClick={selectMethod('upload')}
            onKeyDown={selectOnKey('upload')}
            style={{marginBottom:"5rem"}}
          >
            <div className="income-method-card__header">
              <div className="income-method-card__heading">
                <i className="fa-solid fa-file-arrow-up" />
                <h5>{t('incomeMethod.uploadTitle')}</h5>
              </div>
            </div>
            <p style={{paddingBottom:"1.5rem"}}>{t('incomeMethod.uploadDescription')}</p>
            <ul className="income-method-card__benefits">
              {uploadBenefits.map((benefit) => (
                <li key={benefit}>
                  <i className="fa-regular fa-check" />
                  {benefit}
                </li>
              ))}
            </ul>
          </div>
      </div>

      <div className="btn-group mobile-reversed space-between" style={{ width: '100%', marginBottom: '2rem' }}>
        <BackButton />
        <AsyncActionButton primary isPending={isSubmittingUpload} onClick={handleContinue}>
          <span>{t('incomeMethod.continue')}</span>
          <i className="fa-regular fa-arrow-right" />
        </AsyncActionButton>
      </div>

      <p className="income-method-footer">{t('incomeMethod.protectedInfo')}</p>
    </>
  )
}

export default ChooseIncomeMethodPage
